import { css } from '@emotion/react'
import styled from '@emotion/styled'
import theme from '../../../styles/theme'
import {
  InfoExtraSmall600Style,
  InfoXXXSmall500Style,
  TextMediumMini400Style,
} from '../../../styles/Text.styles'

// Interfaces for styled component props
interface CellStyleProps {
  center?: boolean
  columns?: number
}

interface CellKeeperStyleProps {
  isLastRow?: boolean
  borderLeft?: boolean
  theme?: any
}

interface GridContainerProps {
  columns: number
}

interface HeaderCellProps {
  isSortable?: boolean
  center?: boolean
  columns?: number
  borderLeft?: boolean
}

interface CellProps {
  highlightColor?: string
  isHighlighted?: boolean
  center?: boolean
  columns?: number
}

interface EmptySpaceProps {
  hasExpansiveList?: boolean
}

interface FullWidthCellProps {
  columns: number
  isLastRow?: boolean
  hideBottomBorder?: boolean
  theme?: any
}

interface GridHeaderContainerProps {
  paddingInGridHeader?: string
}

interface CompactPaginationProps {
  $compact?: boolean
}

export const CellContentStyle = css`
  text-overflow: ellipsis;
  max-width: 100%;
  padding-right: clamp(0.5rem, 2%, 4rem);
  padding-left: clamp(0.5rem, 2%, 4rem);
  overflow: hidden;
`

export const CellStyle = (props: CellStyleProps) => css`
  width: 100%;
  min-height: 2.5rem;
  display: flex;

  align-items: center;
  padding: 0.5rem;

  & > * {
    ${props.center &&
    `
          margin-left: auto;
      `}
    margin-right: auto;
  }

  &:nth-of-type(${props.columns}n+1) {
    & > * {
      margin-left: 0;
      margin-right: auto;
    }
  }
`

export const CellKeeperStyle = (props: CellKeeperStyleProps) => css`
  width: 100%;
  min-width: 0;
  min-height: 3rem;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  ${!props.isLastRow &&
  `
      border-bottom: 1px solid ${props.theme.colors.backgroundDark};
    `}

  ${props.borderLeft &&
  `
      border-left: 1px solid ${props.theme.colors.backgroundDark};
    `}
`

export const GridWrapper = styled.div`
  border-radius: 1rem;
  border: 1px solid ${theme.colors.backgroundDark};
  min-width: 0;
  //background-color: aquamarine; // debug
`

export const GridTop = styled.div`
  width: 100%;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  overflow: hidden;
`

export const GridBottom = styled.div`
  width: 100%;
  min-width: 0;
  background-color: ${theme.colors.surface};
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
`

export const GridArea = styled.div`
  width: 100%;
  background-color: ${theme.colors.surface};
  //overflow-y: auto; // Add scrollbar when content exceeds height
  //overflow-x: hidden; // Prevent horizontal scrolling

  /* Apply border-radius when GridArea is the first or last of its type */
  &:first-of-type {
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    overflow: hidden;
  }

  &:last-of-type {
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
    overflow: hidden;
  }
`

export const GridContainer = styled.div<GridContainerProps>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns}, auto)`};
  width: 100%;
  min-width: 0;
  background-color: ${theme.colors.surface};

  height: auto;
`

export const CellContent = styled.div`
  ${CellContentStyle};
  //background-color: #2a7d2f; // debug
`

export const SortIcon = styled.span`
  margin-left: 8px;

  display: inline-flex;
  flex-direction: column;

  svg {
    font-size: 12px;
  }
`

export const NoItemsMessage = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  width: 100%;
  color: ${theme.colors.fadedText};
`

export const HeaderCellFont = css`
  ${InfoXXXSmall500Style};
`

export const HeaderCell = styled.button<HeaderCellProps>`
  ${(props) => CellKeeperStyle(props)};
  ${HeaderCellFont};
  padding: 2% 2% 5% 2%;
  background-color: ${theme.colors.surfaceSecondary};
  border: none;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  justify-items: right;
  justify-content: space-between;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.backgroundDark};
  transition: background-color 0.2s ease;
  padding-top: 1rem;

  & > * {
    ${(props) =>
      props.center &&
      `
      margin-left: auto;
    `}
    margin-right: auto;
  }

  &:nth-of-type(${(props) => props.columns}n+1) {
    & > * {
      margin-left: 0;
      margin-right: auto;
    }
  }

  ${(props) =>
    props.isSortable &&
    `
    &:hover {
      background-color: ${theme.colors.backgroundDark};
    }

    &.active {
      background-color: ${theme.colors.backgroundTwilight};

      ${SortIcon} {
        opacity: 1;
      }
    }
  `}
`

export const RegularHeaderCell = styled.div<CellProps>`
  ${(props) => CellStyle(props)};
  ${InfoXXXSmall500Style};
  padding: 0.75rem;
  width: 100%;
`

export const Cell = styled.div<CellProps>`
  ${(props) => CellStyle(props)};
  background-color: ${(props) => {
    // If a specific highlight color is provided, use that
    if (props.highlightColor) {
      return props.highlightColor
    }

    // Otherwise, fall back to the standard highlight color or default surface color
    return props.isHighlighted ? '#FFFCF5' : theme.colors.surface
  }};
  //background-color: #f0d9c8; // debug
`

export const CellKeeper = styled.div<CellKeeperStyleProps>`
  ${(props) => CellKeeperStyle(props)};
  background-color: ${theme.colors.surface};
`

export const EmptySpace = styled.div<EmptySpaceProps>`
  width: 100%;
  height: ${(props) => (props.hasExpansiveList ? '1.8rem' : '0rem')};
`

export const PaginationContainer = styled.div<CompactPaginationProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => (props.$compact ? '0.4rem' : '0.75rem')};
  padding: ${(props) => (props.$compact ? '0.65rem 0.75rem' : '1rem')};
  min-width: 0;
`

export const PagesContainer = styled.div<CompactPaginationProps>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => (props.$compact ? '0.25rem' : '0.5rem')};
  justify-content: center;
  flex: 1;
  min-width: 0;
`

export const PageButton = styled.button<CompactPaginationProps>`
  ${InfoExtraSmall600Style};
  background-color: transparent;
  padding: ${(props) => (props.$compact ? '0.35rem 0.55rem' : '0.5rem 1rem')};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: outline background-color 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const NavigationButton = styled(PageButton)`
  min-width: ${(props) => (props.$compact ? '3.5rem' : '5rem')};
  flex-shrink: 0;
  background-color: ${theme.colors.surface};
  outline: 2px solid ${theme.colors.backgroundDark};
  &:hover:not(:disabled) {
    background-color: ${theme.colors.background};
  }
`

export const NumberButton = styled(PageButton)`
  min-width: ${(props) => (props.$compact ? '2rem' : '2.5rem')};
  padding: ${(props) => (props.$compact ? '0.35rem' : '0.5rem')};
  background-color: ${theme.colors.surface};
  color: ${theme.colors.veryVeryVeryFadedText};
  &:hover:not(:disabled) {
    outline: 0.1rem solid ${theme.colors.veryVeryVeryFadedText};
  }

  &.active {
    background: ${theme.colors.secondaryVeryVeryLight};
    color: ${theme.colors.secondaryFaded};
  }
`

export const PageIndicator = styled.span<CompactPaginationProps>`
  ${TextMediumMini400Style};
  padding: ${(props) => (props.$compact ? '0.25rem' : '0.5rem')};
`

export const GridHeaderContainer = styled.div<GridHeaderContainerProps>`
  display: flex;
  padding: ${(props) => props.paddingInGridHeader || '1.5rem 3rem 1.5rem 2rem'};
  gap: 1rem;
`

export const GridHeaderLeft = styled.div`
  flex: 0 0 80%;
  display: flex;
  align-items: center;
`

export const GridHeaderRight = styled.div`
  flex: 0 0 20%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

/* Full width cell that spans across all columns in the grid
 * Uses CSS Grid's grid-column property to span from first to last column
 * props.columns determines the total number of columns to span
 */
export const FullWidthCell = styled.div<FullWidthCellProps>`
  grid-column: 1 / span ${(props) => props.columns};
  width: 100%;

  ${(props) =>
    !props.isLastRow &&
    !props.hideBottomBorder &&
    `
    border-bottom: 1px solid ${theme.colors.backgroundDark};
  `}
`
